import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, FileText, ChevronDown, ChevronUp, Check, Globe, Users, Cookie, Link as LinkIcon, BarChart, Gavel } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

// Accordion Section Component
function AccordionSection({ icon: Icon, title, children, defaultOpen = false }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="mb-6 bg-white dark:bg-slate-900 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100 dark:border-slate-700">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-8 py-6 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 hover:from-slate-100 hover:to-slate-50 dark:hover:from-slate-700 dark:hover:to-slate-900 transition-all duration-300 text-left"
            >
                <div className="flex items-center gap-4 min-w-0">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] text-white shadow-lg shrink-0">
                        <Icon size={24} />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">{title}</h2>
                </div>
                {isOpen ? (
                    <ChevronUp className="text-[#3B2B9A] dark:text-indigo-300 shrink-0 ml-2" size={28} aria-hidden />
                ) : (
                    <ChevronDown className="text-slate-500 dark:text-slate-400 shrink-0 ml-2" size={28} aria-hidden />
                )}
            </button>

            <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-8 py-6 text-slate-700 dark:text-slate-300 leading-relaxed space-y-4 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-950/80">
                    {children}
                </div>
            </div>
        </div>
    );
}

// Main Privacy Policy Component
export default function PrivacyPolicy() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        { icon: Shield, text: "Your data is protected with industry-standard security" },
        { icon: Lock, text: "We never sell your personal information" },
        { icon: Eye, text: "Complete transparency in how we use your data" },
        { icon: Check, text: "Committed to data protection compliance" }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
            <Navbar />

            {/* Hero Section */}
            <div className="pt-32 pb-20 px-6 bg-gradient-to-br from-[#3B2B9A] via-[#5A47D8] to-[#7B68EE] relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-20 w-72 h-72 bg-white dark:bg-slate-900 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-20 w-96 h-96 bg-white dark:bg-slate-900 rounded-full blur-3xl"></div>
                </div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <div className="inline-block p-4 bg-white/20 backdrop-blur-lg rounded-2xl mb-6 shadow-xl">
                        <Shield size={64} className="text-white mx-auto" />
                    </div>
                    <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
                        Privacy Policy
                    </h1>
                    <p className="text-xl md:text-2xl text-white/90 font-light">
                        Your privacy is our priority. Learn how we protect your data.
                    </p>
                    <div className="mt-8 text-white/80 text-sm">
                        Last Updated: January 11, 2026
                    </div>
                </div>
            </div>

            {/* Trust Badges */}
            <div className="max-w-6xl mx-auto px-6 -mt-12 relative z-20">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {features.map((feature, idx) => (
                        <div
                            key={idx}
                            className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-100 dark:border-slate-700"
                        >
                            <feature.icon className="text-[#3B2B9A] dark:text-indigo-300 mb-3" size={32} />
                            <p className="text-slate-700 dark:text-slate-200 font-semibold text-sm">{feature.text}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-5xl mx-auto px-6 py-16">

                {/* Overview Card */}
                <div className="mb-12 bg-gradient-to-br from-[#3B2B9A]/5 to-[#5A47D8]/5 dark:from-slate-900 dark:to-slate-900 rounded-2xl p-8 shadow-lg border border-[#3B2B9A]/20 dark:border-slate-600">
                    <h2 className="text-2xl font-bold text-[#3B2B9A] dark:text-indigo-300 mb-4 flex items-center gap-3">
                        <FileText size={28} />
                        An Overview
                    </h2>
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                        Welcome to the Privacy Policy of <strong className="text-[#3B2B9A] dark:text-indigo-300">Landmarks Realty</strong>.
                    </p>
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                        A real estate consulting company with its registered office at <strong>G-35, Floor 1, Sector 3, Noida – 201303, Uttar Pradesh, India</strong>. This policy applies to our website and social media platforms operated by Landmarks Realty. We are committed to protecting your personal information while delivering real estate consulting services with integrity and security.
                    </p>
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                        This Privacy Policy governs only information collected through our website. Please read it carefully to understand how your data is handled.
                    </p>
                </div>

                {/* Accordion Sections */}
                <AccordionSection icon={Globe} title="Understanding This Privacy Policy" defaultOpen={true}>
                    <p className="mb-4">
                        These terms work together with our Website Terms and Conditions. Depending on how you interact with the site, additional terms may apply.
                    </p>
                    <p className="mb-4">
                        Our physical office address, email, and phone number are provided on the website and our official social media channels. Any contact details you share may be used to communicate service updates or related information, even if an opt-out service like DND is active, where permissible by law.
                    </p>
                </AccordionSection>

                <AccordionSection icon={Users} title="Information Collected from Visitors">
                    <p className="mb-4">
                        We may collect certain non-identifying data, such as:
                    </p>
                    <div className="bg-slate-50 dark:bg-slate-950 rounded-xl p-6">
                        <ul className="space-y-2 ml-6">
                            <li className="flex items-start gap-2">
                                <span className="text-[#3B2B9A] mt-1">•</span>
                                <span>Device type and browser version</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#3B2B9A] mt-1">•</span>
                                <span>Language preference and referral site</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#3B2B9A] mt-1">•</span>
                                <span>Access timestamps and usage patterns</span>
                            </li>
                        </ul>
                    </div>
                    <p className="mt-4">
                        This information helps us improve our website and understand user behavior at a high level. Aggregate, non-identifying data may be shared publicly in reports or summaries. In some cases, IP addresses and similar identifiers may be logged for internal analysis and security, treated with confidentiality.
                    </p>
                </AccordionSection>

                <AccordionSection icon={FileText} title="Collecting Personal Information">
                    <p className="mb-4">
                        Personal details are collected when you choose to provide them, such as when:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-600">
                            <Check className="text-green-600 dark:text-green-400 mb-2" size={20} />
                            <p className="font-semibold text-slate-800 dark:text-white">Submitting an Inquiry</p>
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Property search or consultation requests</p>
                        </div>
                        <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-600">
                            <Check className="text-green-600 dark:text-green-400 mb-2" size={20} />
                            <p className="font-semibold text-slate-800 dark:text-white">Signing Up for Updates</p>
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Newsletter or market updates subscription</p>
                        </div>
                        <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-600">
                            <Check className="text-green-600 dark:text-green-400 mb-2" size={20} />
                            <p className="font-semibold text-slate-800 dark:text-white">Service Requests</p>
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Completing consultation forms</p>
                        </div>
                        <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-600">
                            <Check className="text-green-600 dark:text-green-400 mb-2" size={20} />
                            <p className="font-semibold text-slate-800 dark:text-white">Contact Forms</p>
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Direct communication through our website</p>
                        </div>
                    </div>
                    <p className="text-slate-700 dark:text-slate-300">
                        The specific information collected depends on your interaction—for example, subscribing for updates typically requires a name and email address. <strong className="text-[#3B2B9A] dark:text-indigo-300">We collect only what is necessary</strong> to respond to your request or deliver the service.
                    </p>
                </AccordionSection>

                <AccordionSection icon={Lock} title="How We Protect Your Personal Information">
                    <p className="mb-4">
                        Protecting your data is important to us. We use widely accepted technical safeguards to protect personal information from unauthorized access or misuse.
                    </p>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/80 dark:to-emerald-950/80 rounded-xl p-6 mb-4 border border-green-200 dark:border-green-800">
                        <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                                <div className="mt-1 p-1 bg-green-600 rounded-full">
                                    <Check size={14} className="text-white" />
                                </div>
                                <span className="text-slate-800 dark:text-slate-200"><strong>Technical Safeguards:</strong> Industry-standard security measures to prevent unauthorized access</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <div className="mt-1 p-1 bg-green-600 rounded-full">
                                    <Check size={14} className="text-white" />
                                </div>
                                <span className="text-slate-800 dark:text-slate-200"><strong>Confidential Treatment:</strong> Your information is handled with strict confidentiality protocols</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <div className="mt-1 p-1 bg-green-600 rounded-full">
                                    <Check size={14} className="text-white" />
                                </div>
                                <span className="text-slate-800 dark:text-slate-200"><strong>Secure Storage:</strong> Data stored on protected servers with restricted access</span>
                            </li>
                        </ul>
                    </div>
                    <div className="p-4 bg-amber-50 dark:bg-amber-950/40 border-l-4 border-amber-500 dark:border-amber-600 rounded-r-xl">
                        <p className="text-amber-900 dark:text-amber-100 text-sm">
                            <strong>Important Note:</strong> However, no system can guarantee absolute security. While we work to protect your information, transmission inherently carries some risk. By using our website, you acknowledge this limitation.
                        </p>
                    </div>
                </AccordionSection>

                <AccordionSection icon={BarChart} title="Advertising and Third-Party Content">
                    <p className="mb-4">
                        Our site may use cookies for analytics and to display relevant ads. Cookies are small files stored on your device that allow us and third parties to personalize content based on browsing patterns.
                    </p>
                    <div className="bg-slate-50 dark:bg-slate-950 rounded-xl p-6">
                        <p className="text-slate-700 dark:text-slate-300 mb-3">
                            <strong className="text-[#3B2B9A] dark:text-indigo-300">Important:</strong> This Policy covers cookies used by Landmarks Realty only. We do not control cookies used by external advertisers or their practices.
                        </p>
                    </div>
                </AccordionSection>

                <AccordionSection icon={LinkIcon} title="Third-Party Links">
                    <p className="mb-4">
                        Our website may contain links to external sites that are not operated by us. Once you click a link to a third-party site, you leave our website.
                    </p>
                    <div className="p-4 bg-blue-50 dark:bg-slate-800/90 border-l-4 border-[#3B2B9A] rounded-r-xl">
                        <p className="text-slate-700 dark:text-slate-300">
                            We strongly recommend reviewing the privacy practices of any external site you visit. <strong className="text-[#3B2B9A] dark:text-indigo-300">Landmarks Realty is not responsible</strong> for the content, policies, or practices of third-party sites or services.
                        </p>
                    </div>
                </AccordionSection>

                <AccordionSection icon={BarChart} title="Analyzing Visitor Behavior">
                    <p className="mb-4">
                        We may analyze general usage trends such as:
                    </p>
                    <div className="bg-slate-50 dark:bg-slate-950 rounded-xl p-6">
                        <ul className="space-y-2 ml-6">
                            <li className="flex items-start gap-2">
                                <span className="text-[#3B2B9A] mt-1">•</span>
                                <span>Time spent on pages</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#3B2B9A] mt-1">•</span>
                                <span>Navigation flow and user journeys</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-[#3B2B9A] mt-1">•</span>
                                <span>Popular content and features</span>
                            </li>
                        </ul>
                    </div>
                    <p className="mt-4 text-slate-700 dark:text-slate-300">
                        Such analytics help us refine the user experience. Any data shared publicly is aggregated and <strong className="text-[#3B2B9A] dark:text-indigo-300">does not include personal identifiers</strong>.
                    </p>
                </AccordionSection>

                <AccordionSection icon={Cookie} title="Personalizing Your Experience with Cookies">
                    <p className="mb-4">
                        Landmarks Realty uses cookies and similar technologies to make the website more user-friendly and to remember preferences.
                    </p>
                    
                    <div className="grid gap-4 mb-4">
                        <div className="bg-purple-50 dark:bg-slate-800/90 rounded-xl p-5 border border-purple-200 dark:border-slate-600">
                            <h4 className="font-bold text-slate-800 dark:text-white mb-2">Cookie Management</h4>
                            <p className="text-slate-600 dark:text-slate-300 text-sm">You may choose to disable cookies in your browser settings. Some features may not function as intended if cookies are disabled.</p>
                        </div>
                        <div className="bg-indigo-50 dark:bg-slate-800/90 rounded-xl p-5 border border-indigo-200 dark:border-slate-600">
                            <h4 className="font-bold text-slate-800 dark:text-white mb-2">Your Consent</h4>
                            <p className="text-slate-600 dark:text-slate-300 text-sm">Continued use of our website without changing cookie settings indicates acceptance of their use.</p>
                        </div>
                    </div>
                </AccordionSection>

                <AccordionSection icon={Eye} title="Keeping Up with Privacy Policy Updates">
                    <p className="mb-4">
                        We may update this Privacy Policy at any time. The updated version will be posted here.
                    </p>
                    <div className="p-4 bg-[#3B2B9A]/10 border border-[#3B2B9A]/20 rounded-xl">
                        <p className="text-slate-700 dark:text-slate-300">
                            <strong className="text-[#3B2B9A] dark:text-indigo-300">By continuing to use our website</strong> after changes are made, you accept the revised Privacy Policy.
                        </p>
                    </div>
                </AccordionSection>

                <AccordionSection icon={Gavel} title="Dispute Resolution and Jurisdiction">
                    <p className="mb-4 font-semibold text-slate-800 dark:text-slate-200">
                        Any legal dispute arising from this Privacy Policy or the Terms and Conditions shall be resolved as follows:
                    </p>

                    <div className="space-y-4">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-900 rounded-xl p-6 border border-blue-200 dark:border-slate-600">
                            <div className="flex items-start gap-3 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-[#3B2B9A] text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
                                <h4 className="font-bold text-slate-800 dark:text-white text-lg">Step 1: Amicable Negotiation</h4>
                            </div>
                            <p className="text-slate-700 dark:text-slate-300 ml-11">
                                In case of any disagreement, the parties agree to first attempt resolution through direct discussion. If unresolved within thirty (30) days of written notice, the dispute will move to the next step.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-slate-800 dark:to-slate-900 rounded-xl p-6 border border-purple-200 dark:border-slate-600">
                            <div className="flex items-start gap-3 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-[#5A47D8] text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
                                <h4 className="font-bold text-slate-800 dark:text-white text-lg">Step 2: Binding Arbitration</h4>
                            </div>
                            <p className="text-slate-700 dark:text-slate-300 ml-11">
                                If negotiations fail, the matter will be referred to arbitration by a sole arbitrator appointed by the Company. The arbitrator's decision will be final and binding. Arbitration will be conducted in English and held in <strong>Noida, Uttar Pradesh, India</strong>.
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 bg-slate-800 rounded-xl p-6 text-white">
                        <h4 className="font-bold mb-3 text-lg">Governing Law and Jurisdiction</h4>
                        <p className="text-white/90">
                            This Privacy Policy and related agreements are governed by the <strong>laws of India</strong>. Any legal action or dispute shall be under the exclusive jurisdiction of courts located in <strong>Noida, Uttar Pradesh, India</strong>.
                        </p>
                    </div>
                </AccordionSection>

            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
}